/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  Optional,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {
  DOWN_ARROW,
  END,
  ENTER,
  HOME,
  LEFT_ARROW,
  PAGE_DOWN,
  PAGE_UP,
  RIGHT_ARROW,
  UP_ARROW
} from '../core/keyboard/keycodes';
import {DateAdapter} from '../core/datetime/index';
import {MdDatepickerIntl} from './datepicker-intl';
import {createMissingDateImplError} from './datepicker-errors';
import {MD_DATE_FORMATS, MdDateFormats} from '../core/datetime/date-formats';
import {MATERIAL_COMPATIBILITY_MODE} from '../core';
/**
 * A calendar that is used as part of the datepicker.
 * \@docs-private
 */
export class MdCalendar<D> implements AfterContentInit {
/**
 * A date representing the period (month or year) to start the calendar in.
 */
startAt: D;
/**
 * Whether the calendar should be started in month or year view.
 */
startView: 'month' | 'year' = 'month';
/**
 * The currently selected date.
 */
selected: D;
/**
 * The minimum selectable date.
 */
minDate: D;
/**
 * The maximum selectable date.
 */
maxDate: D;
/**
 * A function used to filter which dates are selectable.
 */
dateFilter: (date: D) => boolean;
/**
 * Emits when the currently selected date changes.
 */
selectedChange = new EventEmitter<D>();
/**
 * Date filter for the month and year views.
 */
_dateFilterForViews = (date: D) => {
    return !!date &&
        (!this.dateFilter || this.dateFilter(date)) &&
        (!this.minDate || this._dateAdapter.compareDate(date, this.minDate) >= 0) &&
        (!this.maxDate || this._dateAdapter.compareDate(date, this.maxDate) <= 0);
  }
/**
 * The current active date. This determines which time period is shown and which date is
 * highlighted when using keyboard navigation.
 * @return {?}
 */
get _activeDate(): D { return this._clampedActiveDate; }
/**
 * @param {?} value
 * @return {?}
 */
set _activeDate(value: D) {
    this._clampedActiveDate = this._dateAdapter.clampDate(value, this.minDate, this.maxDate);
  }
private _clampedActiveDate: D;
/**
 * Whether the calendar is in month view.
 */
_monthView: boolean;
/**
 * The label for the current calendar view.
 * @return {?}
 */
get _periodButtonText(): string {
    return this._monthView ?
        this._dateAdapter.format(this._activeDate, this._dateFormats.display.monthYearLabel)
            .toLocaleUpperCase() :
        this._dateAdapter.getYearName(this._activeDate);
  }
/**
 * @return {?}
 */
get _periodButtonLabel(): string {
    return this._monthView ? this._intl.switchToYearViewLabel : this._intl.switchToMonthViewLabel;
  }
/**
 * The label for the the previous button.
 * @return {?}
 */
get _prevButtonLabel(): string {
    return this._monthView ? this._intl.prevMonthLabel : this._intl.prevYearLabel;
  }
/**
 * The label for the the next button.
 * @return {?}
 */
get _nextButtonLabel(): string {
    return this._monthView ? this._intl.nextMonthLabel : this._intl.nextYearLabel;
  }
/**
 * @param {?} _elementRef
 * @param {?} _intl
 * @param {?} _ngZone
 * @param {?} _isCompatibilityMode
 * @param {?} _dateAdapter
 * @param {?} _dateFormats
 */
constructor(private _elementRef: ElementRef,
private _intl: MdDatepickerIntl,
private _ngZone: NgZone,
public _isCompatibilityMode: boolean,
private _dateAdapter: DateAdapter<D>,
private _dateFormats: MdDateFormats) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
    if (!this._dateFormats) {
      throw createMissingDateImplError('MD_DATE_FORMATS');
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._activeDate = this.startAt || this._dateAdapter.today();
    this._focusActiveCell();
    this._monthView = this.startView != 'year';
  }
/**
 * Handles date selection in the month view.
 * @param {?} date
 * @return {?}
 */
_dateSelected(date: D): void {
    if (!this._dateAdapter.sameDate(date, this.selected)) {
      this.selectedChange.emit(date);
    }
  }
/**
 * Handles month selection in the year view.
 * @param {?} month
 * @return {?}
 */
_monthSelected(month: D): void {
    this._activeDate = month;
    this._monthView = true;
  }
/**
 * Handles user clicks on the period label.
 * @return {?}
 */
_currentPeriodClicked(): void {
    this._monthView = !this._monthView;
  }
/**
 * Handles user clicks on the previous button.
 * @return {?}
 */
_previousClicked(): void {
    this._activeDate = this._monthView ?
        this._dateAdapter.addCalendarMonths(this._activeDate, -1) :
        this._dateAdapter.addCalendarYears(this._activeDate, -1);
  }
/**
 * Handles user clicks on the next button.
 * @return {?}
 */
_nextClicked(): void {
    this._activeDate = this._monthView ?
        this._dateAdapter.addCalendarMonths(this._activeDate, 1) :
        this._dateAdapter.addCalendarYears(this._activeDate, 1);
  }
/**
 * Whether the previous period button is enabled.
 * @return {?}
 */
_previousEnabled(): boolean {
    if (!this.minDate) {
      return true;
    }
    return !this.minDate || !this._isSameView(this._activeDate, this.minDate);
  }
/**
 * Whether the next period button is enabled.
 * @return {?}
 */
_nextEnabled(): boolean {
    return !this.maxDate || !this._isSameView(this._activeDate, this.maxDate);
  }
/**
 * Handles keydown events on the calendar body.
 * @param {?} event
 * @return {?}
 */
_handleCalendarBodyKeydown(event: KeyboardEvent): void {
    // TODO(mmalerba): We currently allow keyboard navigation to disabled dates, but just prevent
    // disabled ones from being selected. This may not be ideal, we should look into whether
    // navigation should skip over disabled dates, and if so, how to implement that efficiently.
    if (this._monthView) {
      this._handleCalendarBodyKeydownInMonthView(event);
    } else {
      this._handleCalendarBodyKeydownInYearView(event);
    }
  }
/**
 * Focuses the active cell after the microtask queue is empty.
 * @return {?}
 */
_focusActiveCell() {
    this._ngZone.runOutsideAngular(() => this._ngZone.onStable.first().subscribe(() => {
      let /** @type {?} */ activeEl = this._elementRef.nativeElement.querySelector('.mat-calendar-body-active');
      activeEl.focus();
    }));
  }
/**
 * Whether the two dates represent the same view in the current view mode (month or year).
 * @param {?} date1
 * @param {?} date2
 * @return {?}
 */
private _isSameView(date1: D, date2: D): boolean {
    return this._monthView ?
        this._dateAdapter.getYear(date1) == this._dateAdapter.getYear(date2) &&
        this._dateAdapter.getMonth(date1) == this._dateAdapter.getMonth(date2) :
        this._dateAdapter.getYear(date1) == this._dateAdapter.getYear(date2);
  }
/**
 * Handles keydown events on the calendar body when calendar is in month view.
 * @param {?} event
 * @return {?}
 */
private _handleCalendarBodyKeydownInMonthView(event: KeyboardEvent): void {
    switch (event.keyCode) {
      case LEFT_ARROW:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate, -1);
        break;
      case RIGHT_ARROW:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate, 1);
        break;
      case UP_ARROW:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate, -7);
        break;
      case DOWN_ARROW:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate, 7);
        break;
      case HOME:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate,
            1 - this._dateAdapter.getDate(this._activeDate));
        break;
      case END:
        this._activeDate = this._dateAdapter.addCalendarDays(this._activeDate,
            (this._dateAdapter.getNumDaysInMonth(this._activeDate) -
             this._dateAdapter.getDate(this._activeDate)));
        break;
      case PAGE_UP:
        this._activeDate = event.altKey ?
            this._dateAdapter.addCalendarYears(this._activeDate, -1) :
            this._dateAdapter.addCalendarMonths(this._activeDate, -1);
        break;
      case PAGE_DOWN:
        this._activeDate = event.altKey ?
            this._dateAdapter.addCalendarYears(this._activeDate, 1) :
            this._dateAdapter.addCalendarMonths(this._activeDate, 1);
        break;
      case ENTER:
        if (this._dateFilterForViews(this._activeDate)) {
          this._dateSelected(this._activeDate);
          // Prevent unexpected default actions such as form submission.
          event.preventDefault();
        }
        return;
      default:
        // Don't prevent default or focus active cell on keys that we don't explicitly handle.
        return;
    }

    this._focusActiveCell();
    // Prevent unexpected default actions such as form submission.
    event.preventDefault();
  }
/**
 * Handles keydown events on the calendar body when calendar is in year view.
 * @param {?} event
 * @return {?}
 */
private _handleCalendarBodyKeydownInYearView(event: KeyboardEvent): void {
    switch (event.keyCode) {
      case LEFT_ARROW:
        this._activeDate = this._dateAdapter.addCalendarMonths(this._activeDate, -1);
        break;
      case RIGHT_ARROW:
        this._activeDate = this._dateAdapter.addCalendarMonths(this._activeDate, 1);
        break;
      case UP_ARROW:
        this._activeDate = this._prevMonthInSameCol(this._activeDate);
        break;
      case DOWN_ARROW:
        this._activeDate = this._nextMonthInSameCol(this._activeDate);
        break;
      case HOME:
        this._activeDate = this._dateAdapter.addCalendarMonths(this._activeDate,
            -this._dateAdapter.getMonth(this._activeDate));
        break;
      case END:
        this._activeDate = this._dateAdapter.addCalendarMonths(this._activeDate,
            11 - this._dateAdapter.getMonth(this._activeDate));
        break;
      case PAGE_UP:
        this._activeDate =
            this._dateAdapter.addCalendarYears(this._activeDate, event.altKey ? -10 : -1);
        break;
      case PAGE_DOWN:
        this._activeDate =
            this._dateAdapter.addCalendarYears(this._activeDate, event.altKey ? 10 : 1);
        break;
      case ENTER:
        this._monthSelected(this._activeDate);
        break;
      default:
        // Don't prevent default or focus active cell on keys that we don't explicitly handle.
        return;
    }

    this._focusActiveCell();
    // Prevent unexpected default actions such as form submission.
    event.preventDefault();
  }
/**
 * Determine the date for the month that comes before the given month in the same column in the
 * calendar table.
 * @param {?} date
 * @return {?}
 */
private _prevMonthInSameCol(date: D): D {
    // Determine how many months to jump forward given that there are 2 empty slots at the beginning
    // of each year.
    let /** @type {?} */ increment = this._dateAdapter.getMonth(date) <= 4 ? -5 :
        (this._dateAdapter.getMonth(date) >= 7 ? -7 : -12);
    return this._dateAdapter.addCalendarMonths(date, increment);
  }
/**
 * Determine the date for the month that comes after the given month in the same column in the
 * calendar table.
 * @param {?} date
 * @return {?}
 */
private _nextMonthInSameCol(date: D): D {
    // Determine how many months to jump forward given that there are 2 empty slots at the beginning
    // of each year.
    let /** @type {?} */ increment = this._dateAdapter.getMonth(date) <= 4 ? 7 :
        (this._dateAdapter.getMonth(date) >= 7 ? 5 : 12);
    return this._dateAdapter.addCalendarMonths(date, increment);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-calendar',
  templateUrl: 'calendar.html',
  styleUrls: ['calendar.css'],
  host: {
    'class': 'mat-calendar',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: MdDatepickerIntl, },
{type: NgZone, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MATERIAL_COMPATIBILITY_MODE, ] }, ]},
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_DATE_FORMATS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'startAt': [{ type: Input },],
'startView': [{ type: Input },],
'selected': [{ type: Input },],
'minDate': [{ type: Input },],
'maxDate': [{ type: Input },],
'dateFilter': [{ type: Input },],
'selectedChange': [{ type: Output },],
};
}

function MdCalendar_tsickle_Closure_declarations() {
/** @type {?} */
MdCalendar.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdCalendar.ctorParameters;
/** @type {?} */
MdCalendar.propDecorators;
/**
 * A date representing the period (month or year) to start the calendar in.
 * @type {?}
 */
MdCalendar.prototype.startAt;
/**
 * Whether the calendar should be started in month or year view.
 * @type {?}
 */
MdCalendar.prototype.startView;
/**
 * The currently selected date.
 * @type {?}
 */
MdCalendar.prototype.selected;
/**
 * The minimum selectable date.
 * @type {?}
 */
MdCalendar.prototype.minDate;
/**
 * The maximum selectable date.
 * @type {?}
 */
MdCalendar.prototype.maxDate;
/**
 * A function used to filter which dates are selectable.
 * @type {?}
 */
MdCalendar.prototype.dateFilter;
/**
 * Emits when the currently selected date changes.
 * @type {?}
 */
MdCalendar.prototype.selectedChange;
/**
 * Date filter for the month and year views.
 * @type {?}
 */
MdCalendar.prototype._dateFilterForViews;
/** @type {?} */
MdCalendar.prototype._clampedActiveDate;
/**
 * Whether the calendar is in month view.
 * @type {?}
 */
MdCalendar.prototype._monthView;
/** @type {?} */
MdCalendar.prototype._elementRef;
/** @type {?} */
MdCalendar.prototype._intl;
/** @type {?} */
MdCalendar.prototype._ngZone;
/** @type {?} */
MdCalendar.prototype._isCompatibilityMode;
/** @type {?} */
MdCalendar.prototype._dateAdapter;
/** @type {?} */
MdCalendar.prototype._dateFormats;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
